import { ApolloClient, createHttpLink, InMemoryCache, InMemoryCacheConfig } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import * as Sentry from '@sentry/react';
import { pick } from 'lodash';
import { toast } from 'react-toastify';

import { isLoanNotFoundError, TEAMS_NOT_PERMITTED_ERROR } from '@willow/shared-iso';

import { ButtonLink } from '../Button/ButtonLink';

export type ApolloCacheConfig = InMemoryCacheConfig;

interface AuthorizedApolloClientOptions {
  graphqlEndpoint: string;
  apolloCacheConfig?: ApolloCacheConfig;
  getHeaders: () => Record<string, string>;
  onUnauthorized: () => void;
}

const clientBuildNum = () => {
  const value = document.querySelector('meta[name="build-num"]')?.getAttribute('value');
  // In local development, this environment variable doesn't get set.
  return value === '' ? undefined : value;
};

export const createAuthorizedApolloClient = ({
  graphqlEndpoint,
  apolloCacheConfig,
  getHeaders,
  onUnauthorized,
}: AuthorizedApolloClientOptions) => {
  const httpLink = createHttpLink({
    uri: graphqlEndpoint,
    credentials: 'include',
  });

  const authLink = setContext(() => ({ headers: getHeaders() }));

  const retryLink = new RetryLink({
    // Tries a failed request up to 4 times total, with jittered exponential backoff (~1s, ~2s, ~4s)
    // Only for network connection errors
    delay: {
      initial: 1000,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 4,
      retryIf: (error) => {
        return !!error && error.statusCode !== 504;
      },
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const context = operation.getContext();
    const headers = context?.response?.headers;

    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
      onUnauthorized();
    }

    // When there is a network error or when user is not authenticated
    // headers will be null
    if (!headers) {
      return;
    }

    const serverBuildNumber = headers.get('Build-Number');

    if (graphQLErrors) {
      const clientBuildNumber = clientBuildNum();
      // In flex/staging/production clientBuildNumber and serverBuildNumber will be populated
      // from CI.  In local dev they'll be empty.
      if (clientBuildNumber && serverBuildNumber && clientBuildNumber !== serverBuildNumber) {
        toast.error(
          <div>
            <ButtonLink
              onClick={(e) => {
                e.preventDefault();
                window.location.reload();
              }}
            >
              Willow has been updated. Click here to refresh now.
            </ButtonLink>
          </div>,
          { autoClose: false, closeOnClick: false },
        );
      }

      for (const gqlError of graphQLErrors) {
        // Don't send QuietValidationError to Sentry as it's a handled validation error.
        // Don't send loan-not-found errors either, the UI renders a "Loan not found" page for those.
        const isQuietError = gqlError.extensions?.errorType === 'QuietValidationError';
        const isHandledLoanNotFound = isLoanNotFoundError(gqlError.message);
        if (!isQuietError && !isHandledLoanNotFound) {
          Sentry.captureMessage(JSON.stringify(pick(gqlError, 'message', 'path')), 'error');
        }

        if (gqlError.message.includes(TEAMS_NOT_PERMITTED_ERROR)) {
          toast.error(
            'You do not have permission to complete this action. Please contact your team administrator for access.',
          );
        }
      }
    }
  });

  return new ApolloClient({
    link: authLink.concat(retryLink).concat(errorLink).concat(httpLink),
    cache: new InMemoryCache(apolloCacheConfig || {}),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
